import React, { Component } from "react";
import DatePicker from "react-datepicker";
import axios from "axios";
import NumberFormat from "react-number-format";
import { APIURL } from "../../config/config";
import { AlertWarning } from "../Alert/Alert";
import moment from "moment";
import Loading from "../Loading";
import * as XLSX from "xlsx-js-style";
import * as FileSaver from "file-saver";
import { ExcelUtil } from "../../utils/excelUtil";
import { format } from "date-fns";

// Config: Show/hide กำไร/ขาดทุน column in Excel
const SHOW_PROFIT_LOSS_COLUMN = false; // Set to true to show

class SparePartsCostByRetailer extends Component {
  constructor(props) {
    super(props);

    // Set default date range: start from 1st day of current month, end at current date
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    this.state = {
      date_start: firstDayOfMonth,
      date_end: now,
      retailerData: [],
      isLoading: false,
    };

    this.handleDateStart = this.handleDateStart.bind(this);
    this.handleDateEnd = this.handleDateEnd.bind(this);
    this.loadSparePartsCostByRetailer =
      this.loadSparePartsCostByRetailer.bind(this);
    this.exportToExcel = this.exportToExcel.bind(this);
  }

  componentDidMount() {
    this.loadSparePartsCostByRetailer();
  }

  handleDateStart(date) {
    this.setState({
      date_start: date,
    });
  }

  handleDateEnd(date) {
    this.setState({
      date_end: date,
    });
  }

  loadSparePartsCostByRetailer() {
    if (this.state.date_start > this.state.date_end) {
      AlertWarning("วันที่เริ่มต้น มากกว่า วันที่สิ้นสุด กรุณาตรวจสอบ");
      return;
    }

    let date_start = moment(this.state.date_start).format("YYYY-MM-DD");
    let date_end = moment(this.state.date_end).format("YYYY-MM-DD");

    let dataReq = {
      date_start,
      date_end,
    };

    // stock_in/cost

    let url = APIURL + "/stock_in/costbyretailer";

    this.setState({
      isLoading: true,
    });

    axios
      .post(url, dataReq)
      .then((res) => {
        if (res.status === 200) {
          this.setState({
            retailerData: res.data,
            isLoading: false,
          });
        }
      })
      .catch((error) => {
        console.log("error:", error);
        this.setState({
          isLoading: false,
        });
      });
  }

  exportToExcel() {
    const { retailerData, date_start, date_end } = this.state;

    // Prepare title rows - each on separate line
    let titleRow = [];

    // Row 1: Report name
    titleRow.push({
      A: "รายงานต้นทุนอะไหล่ตามร้านค้า",
    });

    // Row 2: empty
    titleRow.push({});

    // Row 3: Date range
    let dateDisplay = moment(date_start).isSame(moment(date_end), "day")
      ? `ประจำวันที่ ${moment(date_start).locale("th").format("LL")}`
      : `ตั้งแต่วันที่ ${moment(date_start)
          .locale("th")
          .format("LL")} ถึงวันที่ ${moment(date_end)
          .locale("th")
          .format("LL")}`;

    titleRow.push({
      A: dateDisplay,
    });

    // Row 4: empty
    titleRow.push({});

    // Row 5: empty
    titleRow.push({});

    // Row 6: Header row - based on config
    let headerRow = {
      A: "ลำดับ",
      B: "ร้านค้า",
      C: "ยอดซื้อ",
      D: "ยอดค่าซ่อม",
    };
    if (SHOW_PROFIT_LOSS_COLUMN) {
      headerRow.E = "กำไร/ขาดทุน";
    }
    titleRow.push(headerRow);

    // Data rows
    let tableRows = retailerData.map((retailer, index) => {
      let row = {
        A: index + 1,
        B: retailer.retailer_name,
        C: retailer.purchase_total,
        D: retailer.sales_total,
      };
      if (SHOW_PROFIT_LOSS_COLUMN) {
        row.E = retailer.profit_loss;
      }
      return row;
    });

    // Calculate totals
    let totalPurchase = retailerData.reduce(
      (sum, item) => sum + Number(item.purchase_total || 0),
      0
    );
    let totalSales = retailerData.reduce(
      (sum, item) => sum + Number(item.sales_total || 0),
      0
    );
    let totalProfit = totalSales - totalPurchase;

    // Summary row
    let summaryRow = {
      A: "รวมทั้งหมด",
      B: "",
      C: totalPurchase,
      D: totalSales,
    };
    if (SHOW_PROFIT_LOSS_COLUMN) {
      summaryRow.E = totalProfit;
    }

    let reportData = [...titleRow, ...tableRows, summaryRow];

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(reportData, {
      origin: "A1",
      skipHeader: true,
    });

    // Set column widths - based on config
    let wscols = [
      { wch: 8 }, // ลำดับ
      { wch: 35 }, // ร้านค้า
      { wch: 20 }, // ยอดซื้อ
      { wch: 20 }, // ยอดค่าซ่อม
    ];
    if (SHOW_PROFIT_LOSS_COLUMN) {
      wscols.push({ wch: 20 }); // กำไร/ขาดทุน
    }

    // Set row heights - title section has 6 rows
    let wsrows = [
      { hpt: 25 }, // row 1: Report name (taller)
      { hpt: 20 }, // row 2: empty
      { hpt: 20 }, // row 3: date range
      { hpt: 20 }, // row 4: empty
      { hpt: 20 }, // row 5: empty
      { hpt: 25 }, // row 6: header row
    ];
    for (let i = 0; i < tableRows.length + 1; i++) {
      wsrows.push({ hpt: 20 });
    }

    ws["!cols"] = wscols;
    ws["!rows"] = wsrows;

    // Calculate column indices before using in merges
    let dataStartRow = 6;
    let lastRow = tableRows.length + dataStartRow;
    let lastCol = SHOW_PROFIT_LOSS_COLUMN ? 4 : 3;

    // Add merge cells for report name and date range
    let merges = [
      // Merge report name row (row 0) from A to lastCol
      { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
      // Merge date range row (row 2) from A to lastCol
      { s: { r: 2, c: 0 }, e: { r: 2, c: lastCol } },
    ];
    ws["!merges"] = merges;

    // Apply styles

    // Report name title style (row 0, center, bold, larger) - now merged across all columns
    let reportNameCell = ws[XLSX.utils.encode_cell({ r: 0, c: 0 })];
    if (reportNameCell) {
      reportNameCell.s = {
        alignment: { horizontal: "center", wrapText: true },
        font: { bold: true, name: "AngsanaUPC", sz: "18" },
      };
    }

    // Date range style (row 2, center) - now merged across all columns
    let dateCell = ws[XLSX.utils.encode_cell({ r: 2, c: 0 })];
    if (dateCell) {
      dateCell.s = {
        alignment: { horizontal: "center", wrapText: true },
        font: { bold: false, name: "AngsanaUPC", sz: "16" },
      };
    }

    // Header row (bold center with border) - row 5 (index 5)
    let rangeHeader = { s: { r: 5, c: 0 }, e: { r: 5, c: lastCol } };
    ExcelUtil.setCellBoldWithBorder(ws, rangeHeader);

    // Order numbers (center with border) - from row 6 to lastRow
    let rangeOrder = { s: { r: 6, c: 0 }, e: { r: lastRow, c: 0 } };
    ExcelUtil.setCellCenterNormalWithBorder(ws, rangeOrder);

    // Retailer names (normal with border) - from row 6 to lastRow
    let rangeRetailer = { s: { r: 6, c: 1 }, e: { r: lastRow, c: 1 } };
    ExcelUtil.setCellNormalWithBorder(ws, rangeRetailer);

    // Numbers (right aligned with border and number format) - from row 6 to lastRow
    let rangeNumber = { s: { r: 6, c: 2 }, e: { r: lastRow, c: lastCol } };
    ExcelUtil.setCellNumberFormatRightWithBorder(ws, rangeNumber);

    // Summary row (bold right with border)
    let rangeSum = { s: { r: lastRow, c: 0 }, e: { r: lastRow, c: lastCol } };
    ExcelUtil.setCellNumberFormatRightBoldWithBorder(ws, rangeSum);

    // Write and save
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const fileExtension = ".xlsx";
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });

    let currFileName = format(new Date(), "yyyy-MM-dd_HH-mm-ss");
    let fileName = "spareparts_cost_by_retailer_" + currFileName;
    FileSaver.saveAs(data, fileName + fileExtension);
  }

  render() {
    // Calculate totals
    let totalPurchase = this.state.retailerData.reduce(
      (sum, item) => sum + Number(item.purchase_total || 0),
      0
    );
    let totalSales = this.state.retailerData.reduce(
      (sum, item) => sum + Number(item.sales_total || 0),
      0
    );
    let totalProfit = totalSales - totalPurchase;

    let retailerRows = this.state.retailerData.map((retailer, index) => {
      let profitClass =
        retailer.profit_loss >= 0 ? "text-success" : "text-danger";

      return (
        <tr key={index}>
          <td className="text-center">{index + 1}</td>
          <td>{retailer.retailer_name}</td>
          <td className="text-right">
            <a
              href={`#/sparepart?retailer_id=${retailer.retailer_id}&tab=8&date_start=${moment(this.state.date_start).format("YYYY-MM-DD")}&date_end=${moment(this.state.date_end).format("YYYY-MM-DD")}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                cursor: "pointer",
                color: "#20a8d8",
                textDecoration: "none",
              }}
              onMouseEnter={(e) =>
                (e.target.style.textDecoration = "underline")
              }
              onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
            >
              <NumberFormat
                displayType={"text"}
                thousandSeparator={true}
                decimalScale={2}
                fixedDecimalScale
                value={retailer.purchase_total}
              />
            </a>
          </td>
          <td className="text-right">
            <NumberFormat
              displayType={"text"}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale
              value={retailer.sales_total}
            />
          </td>
          {/* <td className={`text-right ${profitClass}`}>
            <NumberFormat
              displayType={"text"}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale
              value={retailer.profit_loss}
            />
          </td> */}
        </tr>
      );
    });

    return (
      <>
        <Loading isLoading={this.state.isLoading} />

        <div className="card card-accent-warning">
          <div className="card-header">
            <strong className="text-title">ต้นทุนอะไหล่ตามร้านค้า</strong>
          </div>
          <div className="card-block">
            {/* Date Range Filter */}
            <div className="row mb-3">
              <div className="col-2 text-right pt-1">จากวันที่</div>
              <div className="col-2">
                <DatePicker
                  selected={this.state.date_start}
                  onChange={this.handleDateStart}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  wrapperClassName="datePickerWrapper"
                />
              </div>
              <div className="col-1 text-right pt-1">ถึง</div>
              <div className="col-2">
                <DatePicker
                  selected={this.state.date_end}
                  onChange={this.handleDateEnd}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  wrapperClassName="datePickerWrapper"
                />
              </div>
              <div className="col-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => this.loadSparePartsCostByRetailer()}
                >
                  <i className="icon-magnifier" /> ตกลง
                </button>{" "}
                <button
                  type="button"
                  className="btn btn-success btn-sm ml-2"
                  onClick={() => this.exportToExcel()}
                >
                  <i className="fa fa-file-excel" /> Excel
                </button>
              </div>
              <div className="col-1"></div>
            </div>

            {/* Date Display */}
            <div className="row mb-2">
              <div className="col-12">
                {moment(this.state.date_start).isSame(
                  this.state.date_end,
                  "day"
                ) ? (
                  <h4>
                    รายงานวันที่{" "}
                    {moment(this.state.date_start).locale("th").format("LL")}
                  </h4>
                ) : (
                  <h4>
                    รายงานตั้งแต่วันที่{" "}
                    {moment(this.state.date_start).locale("th").format("LL")}{" "}
                    ถึงวันที่{" "}
                    {moment(this.state.date_end).locale("th").format("LL")}
                  </h4>
                )}
              </div>
            </div>

            {/* Data Table */}
            <div className="row">
              <div className="col-12">
                <table className="table table-striped table-responsive">
                  <thead>
                    <tr>
                      <th className="text-center" style={{ width: "50px" }}>
                        ลำดับ
                      </th>
                      <th style={{ width: "200px" }}>ร้านค้า</th>
                      <th className="text-right" style={{ width: "120px" }}>
                        ยอดซื้อ
                      </th>
                      <th className="text-right" style={{ width: "130px" }}>
                        ยอดค่าซ่อม
                      </th>
                      {/* <th className="text-right" style={{ width: "120px" }}>
                        กำไร/ขาดทุน
                      </th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {retailerRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-danger text-center">
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    ) : null}
                    {retailerRows}
                    {retailerRows.length > 0 ? (
                      <tr className="table-secondary font-weight-bold">
                        <td colSpan={2} className="text-right">
                          รวมทั้งหมด
                        </td>
                        <td className="text-right">
                          <NumberFormat
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale
                            value={totalPurchase}
                          />
                        </td>
                        <td className="text-right">
                          <NumberFormat
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale
                            value={totalSales}
                          />
                        </td>
                        {/* <td
                          className={`text-right ${
                            totalProfit >= 0 ? "text-success" : "text-danger"
                          }`}
                        >
                          <NumberFormat
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale
                            value={totalProfit}
                          />
                        </td> */}
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default SparePartsCostByRetailer;
